import type { CallSignal, IceServer } from './types';

/** How long the team's dashboards ring (matches CALL_RING_SECONDS on the API). */
export const RING_SECONDS = 45;
/** Answered but the browsers still can't reach each other: give up. */
const CONNECT_SECONDS = 25;

export type TeamCallState = 'ringing' | 'connecting' | 'live' | 'ended';
/**
 * missed: nobody answered · ended: someone hung up · cancelled: the visitor stopped ringing ·
 * failed: no audio path on this network · mic: microphone blocked · busy: couldn't start.
 */
export type TeamCallEnd = 'missed' | 'ended' | 'cancelled' | 'failed' | 'mic' | 'busy';

export interface TeamCallEvents {
  onState(state: TeamCallState): void;
  onAccepted(agentName: string | null): void;
  onLive(): void;
  onEnded(reason: TeamCallEnd, message: string | null): void;
}

export interface TeamCallDeps {
  start(): Promise<{ callId: string; iceServers: IceServer[] }>;
  /** Hang up or stop ringing (must survive the page closing: `keepalive`). */
  end(callId: string): Promise<void>;
  signal(callId: string, signal: CallSignal): void;
  getUserMedia?: (c: MediaStreamConstraints) => Promise<MediaStream>;
  createPeer?: (config: RTCConfiguration) => RTCPeerConnection;
  playRemote?: (stream: MediaStream) => () => void;
}

export function teamCallSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.RTCPeerConnection === 'function' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/** Plays the agent's voice through a detached <audio> element; returns a stop function. */
function playThroughAudio(stream: MediaStream) {
  const audio = new Audio();
  audio.autoplay = true;
  audio.srcObject = stream;
  void audio.play().catch(() => undefined);
  return () => {
    audio.pause();
    audio.srcObject = null;
  };
}

/** The "ring… ring…" a caller hears while waiting (two tones, 2 s on, 4 s off). */
function ringback(): () => void {
  const Ctx =
    typeof window !== 'undefined'
      ? (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
      : undefined;
  if (!Ctx) return () => undefined;
  let ctx: AudioContext;
  try {
    ctx = new Ctx();
  } catch {
    return () => undefined;
  }
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  const tones = [440, 480].map((f) => {
    const o = ctx.createOscillator();
    o.frequency.value = f;
    o.connect(gain);
    o.start();
    return o;
  });
  const ring = () => {
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.06, t);
    gain.gain.setValueAtTime(0, t + 2);
  };
  ring();
  const timer = setInterval(ring, 6000);
  return () => {
    clearInterval(timer);
    tones.forEach((o) => o.stop());
    void ctx.close().catch(() => undefined);
  };
}

/**
 * A voice call from the visitor to the team, browser to browser (WebRTC). The API rings the
 * team and relays the connection setup; audio goes directly to the agent's browser, or through
 * our TURN relay when the networks don't allow a direct path. The visitor makes the offer.
 */
export class TeamCall {
  private callId: string | null = null;
  private iceServers: IceServer[] = [];
  private mic: MediaStream | null = null;
  private peer: RTCPeerConnection | null = null;
  private pending: RTCIceCandidateInit[] = [];
  private stopAudio: () => void = () => undefined;
  private stopRing: () => void = () => undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private state: TeamCallState = 'ringing';

  constructor(
    private readonly deps: TeamCallDeps,
    private readonly events: TeamCallEvents,
  ) {}

  get id() {
    return this.callId;
  }

  async start() {
    this.setState('ringing');
    try {
      const getUserMedia =
        this.deps.getUserMedia ?? ((c) => navigator.mediaDevices.getUserMedia(c));
      this.mic = await getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      return this.finish('mic', 'Allow the microphone in your browser to call us.');
    }
    if (this.over()) return this.release();
    try {
      const joined = await this.deps.start();
      this.callId = joined.callId;
      this.iceServers = joined.iceServers;
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : null;
      return this.finish('busy', message ?? "We couldn't place the call. Please try again.");
    }
    // Hung up while the call was being placed.
    if (this.over()) return void this.deps.end(this.callId).catch(() => undefined);
    this.stopRing = ringback();
    this.timer = setTimeout(() => this.hangUp('missed'), RING_SECONDS * 1000);
  }

  /** An agent answered: connect the two browsers. */
  async accepted(callId: string, agentName: string | null) {
    if (callId !== this.callId || this.state !== 'ringing') return;
    this.stopRing();
    clearTimeout(this.timer);
    this.events.onAccepted(agentName);
    this.setState('connecting');
    this.timer = setTimeout(() => this.hangUp('failed'), CONNECT_SECONDS * 1000);

    const create = this.deps.createPeer ?? ((c) => new RTCPeerConnection(c));
    const peer = create({ iceServers: this.iceServers });
    this.peer = peer;
    for (const track of this.mic?.getTracks() ?? []) peer.addTrack(track, this.mic!);
    peer.onicecandidate = (e) => {
      if (e.candidate && this.callId) {
        const c = e.candidate.toJSON();
        this.deps.signal(this.callId, {
          type: 'candidate',
          candidate: {
            candidate: c.candidate ?? '',
            sdpMid: c.sdpMid ?? null,
            sdpMLineIndex: c.sdpMLineIndex ?? null,
          },
        });
      }
    };
    peer.ontrack = (e) => {
      this.stopAudio();
      this.stopAudio = (this.deps.playRemote ?? playThroughAudio)(
        e.streams[0] ?? new MediaStream([e.track]),
      );
    };
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === 'connected' && this.state === 'connecting') {
        clearTimeout(this.timer);
        this.setState('live');
        this.events.onLive();
      } else if (peer.connectionState === 'failed') {
        this.hangUp('failed');
      }
    };
    try {
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      this.deps.signal(callId, { type: 'offer', sdp: offer.sdp ?? '' });
    } catch {
      this.hangUp('failed');
    }
  }

  /** The agent's answer and network candidates. */
  async signal(callId: string, signal: CallSignal) {
    const peer = this.peer;
    if (callId !== this.callId || !peer) return;
    try {
      if (signal.type === 'answer') {
        await peer.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
        for (const c of this.pending.splice(0)) await peer.addIceCandidate(c);
      } else if (signal.type === 'candidate') {
        if (peer.remoteDescription) await peer.addIceCandidate(signal.candidate);
        else this.pending.push(signal.candidate);
      }
    } catch {
      // A bad candidate is skipped; the connection timeout catches a call that never connects.
    }
  }

  /** The API ended the call (the agent hung up, or nobody answered). */
  ended(callId: string, reason: 'missed' | 'ended') {
    if (callId !== this.callId || this.state === 'ended') return;
    this.finish(reason, null);
  }

  setMuted(muted: boolean) {
    for (const track of this.mic?.getAudioTracks() ?? []) track.enabled = !muted;
  }

  /** The visitor hangs up (or the call gave up): tells the API, which tells the agent. */
  hangUp(reason?: TeamCallEnd) {
    if (this.state === 'ended') return;
    const why = reason ?? (this.state === 'ringing' ? 'cancelled' : 'ended');
    if (this.callId) void this.deps.end(this.callId).catch(() => undefined);
    this.finish(
      why,
      why === 'failed' ? "The call couldn't connect on this network. Please try again." : null,
    );
  }

  private finish(reason: TeamCallEnd, message: string | null) {
    this.setState('ended');
    this.release();
    this.events.onEnded(reason, message);
  }

  private release() {
    clearTimeout(this.timer);
    this.stopRing();
    this.stopAudio();
    this.peer?.close();
    this.peer = null;
    for (const track of this.mic?.getTracks() ?? []) track.stop();
    this.mic = null;
  }

  /** Hung up meanwhile (checked after each await). */
  private over() {
    return this.state === 'ended';
  }

  private setState(state: TeamCallState) {
    this.state = state;
    this.events.onState(state);
  }
}
